import React, { useEffect, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { portalAPI } from '../../services/api';
import { notify, reportLoadError } from '../../utils/notify';
import { formatCurrency } from '../../utils/formatCurrency';
import { generatePayslipPDF } from '../../utils/generatePayslipPDF';
import StatusBadge from '../../components/StatusBadge';

// The guard's payslips once approved, newest first; each one as the same
// PDF HR prints, made from the guard's own data (/api/portal/payslips/:id).

const STATUS_LABEL = { approved: 'Approved', consolidated: 'Approved', paid: 'Paid' };

const PortalPayslips = () => {
  const { t, locale, language } = useLanguage();
  const [slips, setSlips] = useState(null);
  const [busy, setBusy] = useState(null);
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    let cancelled = false;
    portalAPI.payslips()
      .then((res) => { if (!cancelled) setSlips(res.data.payslips); })
      .catch((error) => reportLoadError(error, tRef.current('Error loading your payslips'), { main: true }));
    return () => { cancelled = true; };
  }, []);

  const download = async (id) => {
    try {
      setBusy(id);
      const res = await portalAPI.payslip(id);
      await generatePayslipPDF(res.data.payslip, null, null, language);
    } catch (error) {
      notify(error.response?.data?.message || t('Error making the PDF'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const date = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="pt-page">
      <h1 className="pt-title">{t('My payslips')}</h1>
      {!slips ? <p className="pt-empty">{t('Loading...')}</p> : slips.length === 0 ? (
        <p className="pt-empty">{t('No payslips yet')}</p>
      ) : (
        <ul className="pt-list">
          {slips.map((s) => (
            <li key={s.id} className="pt-card pt-row">
              <div>
                <div className="pt-row__title">{date(s.period_start)} – {date(s.period_end)}</div>
                <div className="pt-amount num">{formatCurrency(s.net_pay, s.currency || 'SRD', locale)}</div>
                <StatusBadge status={s.status === 'paid' ? 'paid' : 'approved'}>{t(STATUS_LABEL[s.status] || s.status)}</StatusBadge>
              </div>
              <button type="button" className="btn btn--secondary pt-big" onClick={() => download(s.id)} disabled={busy === s.id}>
                <Download aria-hidden="true" /> {t('PDF')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PortalPayslips;
